import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Switch, TextField, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { handleErrorToast } from 'src/components/molecules';
import {
  useCreateCategoryMutation,
  useGetCategoryConfigCategoriesQuery,
} from 'src/graphql/generated';
import { FeatureFlag } from 'src/types';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import EditorBox from 'src/components/admin/configuration/EditorBox';
import { EditorValue } from 'src/lib/rich-text-editor';
import { buildCategoryFieldsPayload } from '../helpers/buildCategoryFieldsPayload';
import { computeCreateCategoryHasUnsavedChanges } from '../helpers/computeCreateCategoryHasUnsavedChanges';
import {
  CHANNEL_FEATURE_FLAGS,
  DEFAULT_REPORTED_FIELDS,
  SpeakUpReportedFieldsValue,
} from '../types';
import { CategoryDrawerShell } from '../molecules';
import { DiscardChangesModal } from './DiscardChangesModal';
import { SpeakUpReportedCategoryFields } from './SpeakUpReportedCategoryFields';

const MAX_NAME_LENGTH = 140;
const DEFAULT_LOCALE = 'en-GB';

interface CreateCategoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (status?: string) => void;
}

export const CreateCategoryDrawer: React.FC<CreateCategoryDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const goTogetherEnabled = useIsFeatureEnabled(FeatureFlag.GO_TOGETHER);
  const dynamicQuestionsV1 = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );
  const vaultTalkEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.vaultTalk,
  );
  const slackEntitled = useIsFeatureEnabled(CHANNEL_FEATURE_FLAGS.slack);
  const reportingPortalEntitled = useIsFeatureEnabled(
    CHANNEL_FEATURE_FLAGS.reportingPortal,
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState<EditorValue>(
    EditorValue.createFromString('', 'html'),
  );
  const [isDetermined, setIsDetermined] = useState(false);
  const [isReported, setIsReported] = useState(false);
  const [reportedFields, setReportedFields] =
    useState<SpeakUpReportedFieldsValue>({
      ...DEFAULT_REPORTED_FIELDS,
      channels: { vaultTalk: false, slack: false, reportingPortal: false },
      goTogether: false,
    });
  const [nameError, setNameError] = useState('');
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const color = tokens.semantic.color;

  const queryClient = useQueryClient();
  const { data } = useGetCategoryConfigCategoriesQuery({ isCOI: false });
  const categories = data?.getOrganisationCategories ?? [];

  const { mutateAsync: createCategory } = useCreateCategoryMutation();

  const isDuplicateName = (newName: string): boolean => {
    if (!newName.trim()) return false;
    return categories.some(
      (cat) => cat.locales?.[DEFAULT_LOCALE]?.name === newName.trim(),
    );
  };

  const handleNameChange = (value: string) => {
    const trimmed = value.slice(0, MAX_NAME_LENGTH);
    setName(trimmed);
    if (nameError && !isDuplicateName(trimmed)) {
      setNameError('');
    }
  };

  const history = useHistory();

  const handleSave = async (afterClose?: () => void) => {
    if (name.trim() && isDuplicateName(name.trim())) {
      setNameError(
        'Category name already exists. Please enter a unique name or modify the existing category.',
      );
      return;
    }

    try {
      const lastCategory = categories.at(-1);
      const order = lastCategory ? lastCategory.order + 1 : 0;

      await createCategory({
        category: {
          order,
          isCOI: false,
          ...buildCategoryFieldsPayload({
            name,
            isDetermined,
            isReported,
            reportedFields,
            entitlements: {
              goTogetherEnabled,
              vaultTalkEntitled,
              slackEntitled,
              reportingPortalEntitled,
            },
            existingDescription: description.toString('html'),
            ...(!dynamicQuestionsV1 && {
              legacyOverrides: {
                manualIntake: reportedFields.manualIntake,
              },
            }),
          }),
        },
      });

      await queryClient.refetchQueries(['GetCategoryConfigCategories']);
      handleClose();
      afterClose?.();
      const createdStatus =
        reportedFields.questionFlow === 'custom' ? 'Form required' : 'Active';
      onSuccess?.(createdStatus);
    } catch {
      handleErrorToast({ toastMessage: 'Error creating category' });
    }
  };

  const handleClose = () => {
    setName('');
    setDescription(EditorValue.createFromString('', 'html'));
    setIsDetermined(false);
    setIsReported(false);
    setReportedFields({
      ...DEFAULT_REPORTED_FIELDS,
      channels: {
        vaultTalk: false,
        slack: false,
        reportingPortal: false,
      },
      goTogether: false,
    });
    setNameError('');
    setIsDiscardModalOpen(false);
    onClose();
  };

  const hasUnsavedChanges = computeCreateCategoryHasUnsavedChanges({
    name,
    description: description.toString('html'),
    isDetermined,
    isReported,
  });

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      handleClose();
    }
  };

  return (
    <>
      <CategoryDrawerShell
        open={isOpen}
        title="Create new category"
        onRequestClose={handleRequestClose}
        onSave={() => handleSave()}
        saveDisabled={!name.trim() || (!isDetermined && !isReported)}
        testId="create-category-drawer"
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Category name{' '}
              <span style={{ color: color.type.muted.value }}>*</span>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter category name"
            data-testid="create-category-name-input"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={!!nameError}
          />
          {nameError && (
            <Typography
              sx={{
                fontSize: '12px',
                color: color.status.error.default.value,
                mt: '4px',
              }}
              data-testid="create-category-name-error"
            >
              {nameError}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={isDetermined}
                onChange={(e) => setIsDetermined(e.target.checked)}
                data-testid="create-category-determined-toggle"
                sx={{ flexShrink: 0, ml: '8px' }}
                slotProps={{
                  input: {
                    'aria-label': 'Determined category',
                    sx: { width: '100% !important', height: '100% !important' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Determined category
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: color.type.muted.value,
                ml: '8px',
              }}
            >
              This allows the category to be selected in Resolution Hub
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Switch
                checked={isReported}
                onChange={(e) => setIsReported(e.target.checked)}
                data-testid="create-category-reported-toggle"
                sx={{ flexShrink: 0, ml: '8px' }}
                slotProps={{
                  input: {
                    'aria-label': 'Reported category',
                    sx: { width: '100% !important', height: '100% !important' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.type.default.value,
                }}
              >
                Reported category
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: color.type.muted.value,
                ml: '8px',
              }}
            >
              This allows the category to be selected by reporters across intake
              channels
            </Typography>
          </Box>
        </Box>

        {isReported && (
          <SpeakUpReportedCategoryFields
            value={reportedFields}
            onChange={setReportedFields}
            goTogetherEnabled={goTogetherEnabled}
            mode="create"
            onGoToFormCreation={() =>
              handleSave(() => history.push('/form-management'))
            }
            hideQuestionFlowTabs={!dynamicQuestionsV1}
          />
        )}

        {isReported && reportedFields.questionFlow === 'default' && (
          <Box>
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
                display: 'block',
                mb: '4px',
              }}
            >
              Category description
            </Typography>
            <EditorBox value={description} onChange={setDescription} />
          </Box>
        )}
      </CategoryDrawerShell>

      <DiscardChangesModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleClose}
      />
    </>
  );
};
